import { API_BASE, getAuthHeaders } from './config';
import {
  requestPushRegistration,
  getPushPermissionState,
  onPushToken,
  isInShell,
  type ShellPushToken,
} from '../shell/bridge';

/**
 * Push device registration.
 * See docs/FeatureDesigns/push-notifications.md §6.2.
 *
 * Web registers the device rather than native, because web owns the session:
 * sign-in is delegated to the shell (it runs the auth sheet) but the bearer
 * token never leaves web, so native has nothing to authenticate a POST with.
 */

/**
 * Ask the shell for a device token and register it with the backend.
 *
 * Fire-and-forget by design — a failure here must never block the flow that
 * triggered it (finishing a session, sending a duel). Returns whether it
 * succeeded, for callers that want to avoid re-prompting.
 */
/** Remembered so sign-out can deregister without re-prompting for a token. */
const INSTALL_ID_KEY = 'push.installId';

export async function registerPushDevice(): Promise<boolean> {
  if (!isInShell()) return false;

  try {
    const device: ShellPushToken = await requestPushRegistration();
    const res = await fetch(`${API_BASE}/push/devices`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(device),
    });
    if (res.ok) {
      try { localStorage.setItem(INSTALL_ID_KEY, device.installId); } catch { /* private mode */ }
    }
    return res.ok;
  } catch {
    // Denied permission, no shell, or a network blip. All are non-events for
    // the caller: the user simply doesn't get push until they're asked again.
    return false;
  }
}

/**
 * Deregister this install on sign-out, so notifications for the departing user
 * stop reaching this device.
 *
 * Best-effort and non-blocking: the backend also reclaims an install when
 * someone else registers it, because sign-out is not guaranteed to run at all
 * (app deleted, crash, expired token). That server-side sweep is the real
 * guarantee; this just makes the common case immediate.
 */
export function deregisterPushDevice(): void {
  if (!isInShell()) return;
  let installId: string | null = null;
  try { installId = localStorage.getItem(INSTALL_ID_KEY); } catch { /* private mode */ }
  if (!installId) return;

  try {
    void fetch(`${API_BASE}/push/devices/${encodeURIComponent(installId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      keepalive: true,   // sign-out may navigate away immediately
    }).catch(() => { /* swallow */ });
    localStorage.removeItem(INSTALL_ID_KEY);
  } catch {
    /* swallow — sign-out must not fail on this */
  }
}

/**
 * Listen for tokens the shell delivers unprompted — it silently re-registers on
 * every launch once permission is granted — and sync each one to the backend.
 *
 * This is what makes device state self-healing. Registration is the only moment
 * the backend hears about an install, so anything that needs repairing there
 * (defaults added after the user first registered, a rotated token, a reclaimed
 * install) is fixed on the next app launch rather than never.
 *
 * Call once, while authenticated. Returns an unsubscribe function.
 */
export function startPushTokenSync(): () => void {
  return onPushToken((device) => {
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/push/devices`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(device),
        });
        if (res.ok) {
          try { localStorage.setItem(INSTALL_ID_KEY, device.installId); } catch { /* private mode */ }
        }
      } catch {
        // Best-effort: a failed sync retries on the next launch by construction.
      }
    })();
  });
}

export interface NotificationCategory {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  required: boolean;
}

/** The full list of notification kinds plus this user's current toggles. */
export async function fetchNotificationCategories(): Promise<NotificationCategory[]> {
  const res = await fetch(`${API_BASE}/notifications/categories`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load notification settings');
  const data = await res.json();
  return (data?.data?.categories ?? data?.categories ?? []) as NotificationCategory[];
}

export async function setNotificationCategory(
  categoryId: string,
  enabled: boolean,
): Promise<void> {
  const res = await fetch(`${API_BASE}/notifications/categories`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ categoryId, enabled }),
  });
  if (!res.ok) throw new Error('Failed to update notification settings');
}

export { getPushPermissionState };
